import { CapacityDetails, CapacityHealth, CapacitySummary } from 'capacity/types'
import {
    CapacityGateway,
    CapacityMemberCounts,
    CapacityMembers,
    CapacityNotSupportedError,
    GatewayCapabilities,
    SavePlan,
    SaveResult,
    TodayUsage,
    VisitorCapacities,
    VisitorKind,
} from './capacityGateway'
import { CLIENT_SIDE_CAPABILITIES, ClientSideCapacityGateway, ClientSideGatewayDeps } from './clientSideGateway'
import { CapacityV2Service } from './capacityV2Service'
import { SERVER_CAPABILITIES, ServerCapacityGateway } from './serverGateway'

export const CAPACITY_SERVER_FLAGS = {
    list: 'CapacityServerList',
    members: 'CapacityServerMembers',
    save: 'CapacityServerSave',
    delete: 'CapacityServerDelete',
    health: 'CapacityServerHealth',
    usage: 'CapacityServerUsage',
    visitors: 'CapacityServerVisitors',
    timeSlots: 'CapacityServerTimeSlots',
} as const

export type CapacityServerFlags = Record<keyof typeof CAPACITY_SERVER_FLAGS, boolean>

export const NO_SERVER_FLAGS: CapacityServerFlags = {
    list: false,
    members: false,
    save: false,
    delete: false,
    health: false,
    usage: false,
    visitors: false,
    timeSlots: false,
}

export const capabilitiesFor = (flags: CapacityServerFlags): GatewayCapabilities => ({
    list: flags.list ? SERVER_CAPABILITIES.list : CLIENT_SIDE_CAPABILITIES.list,
    members: flags.members ? SERVER_CAPABILITIES.members : CLIENT_SIDE_CAPABILITIES.members,
    optionItemsCap: flags.members ? SERVER_CAPABILITIES.optionItemsCap : CLIENT_SIDE_CAPABILITIES.optionItemsCap,
    atomicSave: flags.save ? SERVER_CAPABILITIES.atomicSave : CLIENT_SIDE_CAPABILITIES.atomicSave,
    delete: flags.delete ? SERVER_CAPABILITIES.delete : CLIENT_SIDE_CAPABILITIES.delete,
    usage: flags.usage ? SERVER_CAPABILITIES.usage : CLIENT_SIDE_CAPABILITIES.usage,
    health: flags.health ? SERVER_CAPABILITIES.health : CLIENT_SIDE_CAPABILITIES.health,
    visitors: flags.visitors ? SERVER_CAPABILITIES.visitors : CLIENT_SIDE_CAPABILITIES.visitors,
    timeSlots: flags.timeSlots ? SERVER_CAPABILITIES.timeSlots : CLIENT_SIDE_CAPABILITIES.timeSlots,
})

export interface GatewayDeps extends ClientSideGatewayDeps {
    capacityV2?: CapacityV2Service
}

const serverOrThrow = (deps: GatewayDeps): ServerCapacityGateway => {
    if (!deps.capacityV2) {
        throw new CapacityNotSupportedError('BUIL-797', 'The Capacity v2 service is not configured')
    }
    return new ServerCapacityGateway(deps.capacityV2)
}

export function createCapacityGateway(deps: GatewayDeps, flags: CapacityServerFlags): CapacityGateway {
    const client = new ClientSideCapacityGateway(deps)
    let serverInstance: ServerCapacityGateway | null = null
    const server = (): ServerCapacityGateway => {
        serverInstance = serverInstance ?? serverOrThrow(deps)
        return serverInstance
    }
    return {
        capabilities: capabilitiesFor(flags),
        listCapacities(accountSlug: string): Promise<CapacitySummary[]> {
            return flags.list ? server().listCapacities(accountSlug) : client.listCapacities(accountSlug)
        },
        getCapacity(accountSlug: string, capacityId: string): Promise<CapacityDetails> {
            return flags.list
                ? server().getCapacity(accountSlug, capacityId)
                : client.getCapacity(accountSlug, capacityId)
        },
        getMembers(accountSlug: string, capacityId: string): Promise<CapacityMembers> {
            return flags.members
                ? server().getMembers(accountSlug, capacityId)
                : client.getMembers(accountSlug, capacityId)
        },
        getMemberCounts(accountSlug: string): Promise<CapacityMemberCounts> {
            return flags.list || flags.members
                ? server().getMemberCounts(accountSlug)
                : client.getMemberCounts(accountSlug)
        },
        save(accountSlug: string, plan: SavePlan): Promise<SaveResult> {
            return flags.save ? server().save(accountSlug, plan) : client.save(accountSlug, plan)
        },
        deleteCapacity(): Promise<void> {
            return flags.delete ? server().deleteCapacity() : client.deleteCapacity()
        },
        getTodayUsage(accountSlug: string, capacityIds: string[], date: string): Promise<Record<string, TodayUsage>> {
            return flags.usage
                ? server().getTodayUsage(accountSlug, capacityIds)
                : client.getTodayUsage(accountSlug, capacityIds, date)
        },
        getHealth(details: CapacityDetails): CapacityHealth {
            return flags.list ? server().getHealth(details) : client.getHealth()
        },
        visitorKindOf(capacity: CapacitySummary): VisitorKind | null {
            return flags.visitors ? server().visitorKindOf(capacity) : client.visitorKindOf(capacity)
        },
        getVisitorCapacities(accountSlug: string): Promise<VisitorCapacities> {
            return flags.visitors
                ? server().getVisitorCapacities(accountSlug)
                : client.getVisitorCapacities(accountSlug)
        },
        createVisitorCapacity(accountSlug: string, kind: VisitorKind): Promise<CapacitySummary> {
            return flags.visitors
                ? server().createVisitorCapacity(accountSlug, kind)
                : client.createVisitorCapacity(accountSlug, kind)
        },
    }
}
